import { useCallback, useEffect, useRef, useState } from "react";
import { getUserAudienceListMembersRef } from "../../utils/FirebaseUtils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import useAsyncEffect from "../useAsyncEffect";
import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "../../firebase";
import { fetchNumAudienceListMembers } from "../../utils/audienceListUtils";
import { API } from "../../API";

const PAGE_SIZE = 100;

type useLiveUpdatingListAnalyticsProps = {
  listId: string;
  eager?: boolean; // if true, loads all members on initial fetch (default: paginated)
};

export const useLiveUpdatingListAnalytics = (
  props: useLiveUpdatingListAnalyticsProps
) => {
  const { listId, eager = false } = props;
  const { accountData } = useSelector(getAccountState).account;
  const [liveListContactUids, setLiveListContactUids] = useState<string[]>([]);
  const [totalMembers, setTotalMembers] = useState<number>(0);
  const [loaded, setLoaded] = useState(false);
  const [allUidsLoaded, setAllUidsLoaded] = useState(false);
  const lastDocRef = useRef<DocumentSnapshot | null>(null);
  // Stable ref so loadAllUids early-return doesn't recreate on every UID page
  const liveListContactUidsRef = useRef<string[]>([]);

  useEffect(() => {
    liveListContactUidsRef.current = liveListContactUids;
  }, [liveListContactUids]);

  const reloadCount = useCallback(() => {
    setLoaded(false);
    setAllUidsLoaded(false);
    lastDocRef.current = null;
  }, []);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (_deps, isLatest) => {
      if (!loaded && listId) {
        const audienceListMembersRef = getUserAudienceListMembersRef(
          accountData.uid,
          listId
        );
        const numMembers = await fetchNumAudienceListMembers(
          accountData.uid,
          listId
        );
        setTotalMembers(numMembers);
        if (eager) {
          const snapshot = await getDocs(
            query(audienceListMembersRef, orderBy("createdAt", "desc"))
          );
          if (isLatest()) {
            setLiveListContactUids(snapshot.docs.map((doc) => doc.id));
            setAllUidsLoaded(true);
          }
        } else {
          const snapshot = await getDocs(
            query(
              audienceListMembersRef,
              orderBy("createdAt", "desc"),
              limit(PAGE_SIZE)
            )
          );
          if (isLatest()) {
            const members = snapshot.docs.map((doc) => doc.id);
            lastDocRef.current =
              snapshot.docs[snapshot.docs.length - 1] ?? null;
            setLiveListContactUids(members);
            if (snapshot.docs.length < PAGE_SIZE) {
              setAllUidsLoaded(true);
            }
          }
        }
      }
      setLoaded(true);
    },
    [accountData.uid, eager, listId, loaded]
  );

  const loadMoreUids = useCallback(async () => {
    if (allUidsLoaded || !lastDocRef.current || !listId) return;
    const audienceListMembersRef = getUserAudienceListMembersRef(
      accountData.uid,
      listId
    );
    const q = query(
      audienceListMembersRef,
      orderBy("createdAt", "desc"),
      startAfter(lastDocRef.current),
      limit(PAGE_SIZE)
    );
    const snapshot = await getDocs(q);
    const newMembers = snapshot.docs.map((doc) => doc.id);
    lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
    setLiveListContactUids((prev) => [...prev, ...newMembers]);
    if (snapshot.docs.length < PAGE_SIZE) {
      setAllUidsLoaded(true);
    }
  }, [allUidsLoaded, listId, accountData.uid]);

  // Fetches all member uids in one batch via the backend (Select All)
  const loadAllUids = useCallback(async (): Promise<string[]> => {
    if (allUidsLoaded) return liveListContactUidsRef.current;
    const { uids } = await API.profile.fetchAudienceListMemberUids({
      uid: accountData.uid,
      listId,
    });
    lastDocRef.current = null;
    setLiveListContactUids(uids);
    setAllUidsLoaded(true);
    return uids;
  }, [allUidsLoaded, accountData.uid, listId]);

  return {
    liveListContactUids,
    totalMembers,
    loaded,
    allUidsLoaded,
    reloadCount,
    loadMoreUids,
    loadAllUids,
  };
};
